import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import MyStories from './MyStories';
import PublicStories from './PublicStories';
import CreateStory from './CreateStory';
import './BrowseStories.css';
import FavoritesStories from './FavoriteStories';
import { useSectionRefs } from '../../context/SectionRefsContext';
import { Helmet } from 'react-helmet';

const sections = [
    'public-stories',
    'favorites',
    'my-stories',
    'create-story'
];

const authenticatedOnlySections = [
    false,
    true,
    true,
    true
]; // authenticated only sections (favorites, my-stories, create-story)

const BrowseStories = () => {
    const { isAuthenticated } = useAuth();
    const [myStoriesToBeAdded, setMyStoriesToBeAdded] = useState([]);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setActiveSection } = useSectionRefs();
    const [sectionLoaded, setSectionLoaded] = useState([false, false, false, false]);

    useEffect(() => {
        const scrollToSection = () => {
            const sectionId = window.location.hash.substring(1); // Get the hash without the "#"
            if (sectionId) {
                const section = document.getElementById(sectionId);
                if (section) {
                    let i = 0;
                    const sectionIndex = sections.indexOf(section);
                    while (i < sectionIndex) {
                        if (!sectionLoaded[i]) return;
                        i++;
                    }
                    section.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }

        scrollToSection();
    }, [sectionLoaded]);

    useEffect(() => {
        if (!isAuthenticated) {
            setSectionLoaded(s => s.map((section, index) => authenticatedOnlySections[index] ? true : section));
        }
    }, [isAuthenticated]);

    const addToMyStories = (storyCover) => {
        setMyStoriesToBeAdded(myStoriesToBeAdded.concat([storyCover]));
    }

    const finishCreatingStory = () => {
        const section = document.getElementById("my-stories");
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleSectionLoaded = useCallback((sectionIndex) => {
        setSectionLoaded(s => s.map((section, index) => index === sectionIndex ? true : section));
    }, []);

    return (
        <>
            <Helmet>
                <title>{t('meta.appTitle')}</title>
                <meta name="description" content={t('meta.appDescription')} />

                <meta property="og:title" content={t('meta.appTitle')} />
                <meta property="og:description" content={t('meta.appOgDescription')} />
                <meta property="og:image" content="/images/default-cover.png" />
                <meta property="og:url" content={window.process.env.REACT_FRONTEND_URL} />
                <meta property="og:type" content="website" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={window.process.env.REACT_FRONTEND_URL} />
                <meta name="twitter:title" content={t('meta.appTitle')} />
                <meta name="twitter:description" content={t('meta.appDescription')} />
                <meta name="twitter:image" content={`${window.process.env.REACT_FRONTEND_URL}/images/default-cover.png`} />
            </Helmet>
            <div className="browse-stories">
                {/* Public Stories Section */}
                <PublicStories
                    onLoaded = {handleSectionLoaded}
                    onVisible = {setActiveSection}
                />

                {!isAuthenticated && (
                    <div className="invite-login">
                        <h2 className='story-section-h2'>✨ {t('browseStories.createYourOwnStories')} ✨</h2>
                        <button className="submit-button" onClick={() => navigate('/login')}>
                            {t('browseStories.login')}
                        </button>
                    </div>
                )}

                {/* Favorites Section */}
                {isAuthenticated && (
                    <FavoritesStories
                        onLoaded = {handleSectionLoaded}
                        onVisible = {setActiveSection}
                    />
                )}

                {/* My Stories Section */}
                {isAuthenticated && (
                    <MyStories
                        newStoriesToAdd={myStoriesToBeAdded}
                        onLoaded = {handleSectionLoaded}
                        onVisible = {setActiveSection}
                    />
                )}

                {/* Create a New Story Section */}
                {isAuthenticated && (
                    <CreateStory
                        onSuccess={addToMyStories}
                        onFinish={finishCreatingStory}
                        onLoaded = {handleSectionLoaded}
                        onVisible = {setActiveSection}
                    />
                )}
            </div>
        </>
    );
};

export default BrowseStories;
